---
title: "Record bKash (and other) subscription payments in your CRM automatically"
description: "If your own app or billing system already confirms bKash payments, one webhook call can record each payment against the right customer in NerdCRM, move their next billing date forward and keep your due-payments list accurate. No retyping."
seoTitle: "Record bKash payments in your CRM automatically"
seoDescription: "Send bKash and other payments from your own system to NerdCRM with one webhook call. The payment is matched to the customer and logged."
publishedAt: "2026-12-15"
slug: "record-bkash-payments-automatically"
authorPhoto: "/og/sohel-headshot.jpg"
authorSameAs:
  - { label: "LinkedIn", url: "https://www.linkedin.com/in/mdsohelrana/" }
faq:
  - question: "Does NerdCRM connect to bKash directly?"
    answer: "No. NerdCRM doesn't talk to bKash. Your own backend confirms the payment with bKash as it does today, then calls NerdCRM's payment-received webhook with the payer's phone, the amount and the bKash transaction ID. NerdCRM records it from there."
  - question: "What if our system sends the same payment twice?"
    answer: "Nothing is double-recorded. The transaction ID is the idempotency key: if a payment with that ID already exists in your organization, NerdCRM returns the existing payment with \"duplicate\": true. Retrying with the same transaction ID is safe."
  - question: "Does it only work for bKash?"
    answer: "No. The payment method defaults to \"bkash\" and the currency to BDT, but you can send any method label (Nagad, card, bank transfer) and any ISO currency code."
  - question: "Do API keys cost extra?"
    answer: "No. API keys and integrations are admin features included in the plan, which costs $4 per user per month."
---

# Record bKash (and other) subscription payments in your CRM automatically

If you sell a subscription in Bangladesh, many of your customers pay by bKash every month. The payment shows up in your own app or billing system. Then someone has to open the CRM, find the customer, and type in the amount, the month and the TrxID. When that step gets skipped, the due-payments list goes stale and your team chases customers who have already paid.

NerdCRM has a webhook for exactly this. Your system calls it once after each confirmed payment, and the payment is recorded against the right customer.

## What is and isn't automatic

To be clear up front: NerdCRM doesn't connect to bKash. Your backend keeps handling the bKash checkout and its success callback as it does today. The only new step is a server-to-server call to NerdCRM after the payment is confirmed. Everything after that call happens on its own.

## Set up an API key

An admin creates a key in **Settings → API Keys**. Each key belongs to one organization, so NerdCRM knows which workspace the payment is for without any extra fields. Only a hash of the key is stored, so keep the raw key in your server's environment or secret store, never in browser code. If a key leaks, an admin can regenerate it or delete it.

## The request

Send a `POST` to `/integrations/payment-received` with the key in the `x-api-key` header:

```bash
curl -X POST "$NERDCRM_BASE_URL/integrations/payment-received" \
  -H "Content-Type: application/json" \
  -H "x-api-key: $NERDCRM_API_KEY" \
  -d '{
    "phone": "01700000000",
    "amount": 799,
    "transactionId": "BKA9X7C2QL",
    "customerName": "Test Academy",
    "month": "12",
    "year": 2026,
    "paidAt": "2026-12-03T10:15:00Z"
  }'
```

Three fields are required: `phone`, `amount` (greater than zero) and `transactionId`. The rest are optional:

- `customerName`: needed only when no existing customer matches the phone, but it's safest to always send it.
- `paymentMethod`: defaults to `bkash`.
- `currency`: an ISO 4217 code, defaulting to `BDT`.
- `contactName`: used only if a new contact has to be created.
- `year` and `month`: the billing period (`"01"` to `"12"`), defaulting to the current month.
- `paidAt`: an ISO 8601 timestamp, saved in the payment notes.
- `notes`: free text added to the payment record.

A missing or invalid key returns `401`. Missing required fields, or an unmatched phone with no `customerName`, return `400` with the reason.

## How NerdCRM finds the customer

**Duplicates first.** The `transactionId` is stored as the payment's reference number. If that ID has already been recorded, nothing new is created and the response says `"duplicate": true`. On a timeout or a `5xx`, just retry with the same payload.

**Match by phone.** NerdCRM looks for a contact with that phone number and uses the contact's company. Matching is on digits, and Bengali numerals are converted, so `+8801700000000`, `01700-000000` and `01700000000` all find the same person.

**Pick the deal.** The payment goes on the company's most recent deal in the *Customer Support* pipeline, or its most recent deal in any pipeline if it has none there.

**Create when there's no match.** If no contact matches the phone, NerdCRM looks for a company with the same name (ignoring case) or creates one from `customerName`, and adds a contact. If the company has no deal, it creates one in the Customer Support pipeline at the *Active User* stage. That last step needs that pipeline and stage to exist in your workspace.

**Record and advance.** The payment is saved with its period, amount, currency, method and reference. If the deal has a next billing date, NerdCRM moves it forward by the deal's billing interval (one month unless the recurring line item says otherwise).

The response tells you what happened: the payment, company and deal IDs, plus a `created` object showing whether a company, contact, deal or payment was newly made.

## Every call is logged

NerdCRM logs every call to this endpoint, including failed ones and calls with a bad key. Each log entry keeps the full request body, the HTTP status and the outcome. So if a payment ever goes missing, it can be traced and re-submitted from the stored request instead of being reconstructed from bKash statements.

## Where the payments show up

Recorded payments appear in **Payment History**, with total collected and filters by date range, company and owner, plus a payment history page for each company. The **Due Payments** tab lists Customer Support deals whose next billing date has passed, with the total due, the number of overdue companies and the average days overdue. Since every webhook payment moves the billing date forward, customers who paid drop off that list without anyone touching it.

Admins can also ask [Smart Reports](/blog/smart-reports-plain-english) questions like "total collected by month this year" or "collections by payment method."

API keys and integrations are included in the plan at [$4 per user per month](/pricing). The [live demo](/demo) shows the payment screens with sample data.
